import React, { useState, useEffect } from "react";
import Icon from "../../../../components/AppIcon";

const JobRoleModal = ({ role, onClose, onSave }) => {
  const isEdit = !!role;

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    department: "",
    isEligibleForTronc: true,
    troncPoints: 1.0,
    isManagement: false,
    sortOrder: 0,
    colorCode: "#3b82f6",
    isActive: true,
    updateReason: "",
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Predefined color options
  const colorOptions = [
    { color: "#3b82f6", name: "Blue" },
    { color: "#10b981", name: "Green" },
    { color: "#f59e0b", name: "Amber" },
    { color: "#ef4444", name: "Red" },
    { color: "#8b5cf6", name: "Purple" },
    { color: "#ec4899", name: "Pink" },
    { color: "#14b8a6", name: "Teal" },
    { color: "#6b7280", name: "Gray" },
  ];

  // Common departments - Fixed predefined list
  const STANDARD_DEPARTMENTS = [
    "Service",
    "Bar",
    "Kitchen",
    "Management",
    "Front of House",
    "Back of House",
    "Administration",
  ];

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name || "",
        code: role.code || "",
        description: role.description || "",
        department: role.department || "",
        isEligibleForTronc: role.isEligibleForTronc ?? true,
        troncPoints: role.troncPoints ?? 1.0,
        isManagement: role.isManagement ?? false,
        sortOrder: role.sortOrder || 0,
        colorCode: role.colorCode || "#3b82f6",
        isActive: role.isActive ?? true,
        updateReason: "",
      });
    }
  }, [role]);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.department) {
      newErrors.department = "Department is required";
    }

    if (formData.troncPoints < 0 || formData.troncPoints > 10) {
      newErrors.troncPoints = "Points must be between 0 and 10";
    }

    if (formData.sortOrder < 0) {
      newErrors.sortOrder = "Sort order must be 0 or greater";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-card rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold">
            {isEdit ? "Edit Job Role" : "Create Job Role"}
          </h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]"
        >
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Role Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className={`input w-full ${errors.name ? "input-error" : ""}`}
                placeholder="e.g., Head Waiter"
              />
              {errors.name && (
                <p className="text-error text-sm mt-1">{errors.name}</p>
              )}
            </div>

            {/* Code */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Code
                <span className="text-muted-foreground font-normal ml-2">
                  (auto-generated if empty)
                </span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) =>
                  handleChange("code", e.target.value.toUpperCase())
                }
                className="input w-full font-mono"
                placeholder="e.g., HW"
                maxLength={10}
              />
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Department <span className="text-error">*</span>
              </label>
              <select
                value={formData.department}
                onChange={(e) => handleChange("department", e.target.value)}
                className={`input w-full ${errors.department ? "input-error" : ""}`}
              >
                <option value="">Select a department</option>
                {STANDARD_DEPARTMENTS.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
              {errors.department && (
                <p className="text-error text-sm mt-1">{errors.department}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Department categorizes job roles and employees
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                className="input w-full min-h-[80px]"
                placeholder="Brief description of the role..."
              />
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Color</label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map(({ color, name }) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleChange("colorCode", color)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      formData.colorCode === color
                        ? "border-foreground scale-110"
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: color }}
                    title={name}
                  />
                ))}
                <input
                  type="color"
                  value={formData.colorCode}
                  onChange={(e) => handleChange("colorCode", e.target.value)}
                  className="w-8 h-8 rounded-full cursor-pointer"
                  title="Custom color"
                />
              </div>
            </div>

            {/* TRONC Settings */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Icon name="PoundSterling" size={18} />
                TRONC Settings
              </h3>

              {/* Eligible for TRONC */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium">Eligible for TRONC</label>
                  <p className="text-sm text-muted-foreground">
                    Employees with this role can receive TRONC distributions
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isEligibleForTronc}
                    onChange={(e) =>
                      handleChange("isEligibleForTronc", e.target.checked)
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* TRONC Points */}
              {formData.isEligibleForTronc && (
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    TRONC Points
                    <span className="text-muted-foreground font-normal ml-2">
                      (weighting for distribution)
                    </span>
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="3"
                      step="0.1"
                      value={formData.troncPoints}
                      onChange={(e) =>
                        handleChange("troncPoints", parseFloat(e.target.value))
                      }
                      className="flex-1"
                    />
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={formData.troncPoints}
                      onChange={(e) =>
                        handleChange(
                          "troncPoints",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      className={`input w-20 text-center ${errors.troncPoints ? "input-error" : ""}`}
                    />
                  </div>
                  {errors.troncPoints && (
                    <p className="text-error text-sm mt-1">
                      {errors.troncPoints}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Higher points = larger share of TRONC. 1.0 is standard, 1.5
                    = 50% more, 0.5 = 50% less
                  </p>
                </div>
              )}
            </div>

            {/* Management Role */}
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div>
                <label className="font-medium">Management Role</label>
                <p className="text-sm text-muted-foreground">
                  Mark as supervisory/management position
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isManagement}
                  onChange={(e) =>
                    handleChange("isManagement", e.target.checked)
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Sort Order
                <span className="text-muted-foreground font-normal ml-2">
                  (lower numbers appear first)
                </span>
              </label>
              <input
                type="number"
                min="0"
                value={formData.sortOrder}
                onChange={(e) =>
                  handleChange("sortOrder", parseInt(e.target.value) || 0)
                }
                className={`input w-full ${errors.sortOrder ? "input-error" : ""}`}
              />
              {errors.sortOrder && (
                <p className="text-error text-sm mt-1">{errors.sortOrder}</p>
              )}
            </div>

            {/* Active Status (Edit only) */}
            {isEdit && (
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div>
                  <label className="font-medium">Active</label>
                  <p className="text-sm text-muted-foreground">
                    Inactive roles won't appear in employee assignment
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            )}

            {/* Update Reason (Edit only) */}
            {isEdit && (
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Reason for Change
                  <span className="text-muted-foreground font-normal ml-2">
                    (for audit trail)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.updateReason}
                  onChange={(e) => handleChange("updateReason", e.target.value)}
                  className="input w-full"
                  placeholder="Optional: Why are you making this change?"
                />
              </div>
            )}

            {/* HR Integration Warning */}
            {isEdit && role?.hrProvider && (
              <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <Icon
                    name="AlertTriangle"
                    size={18}
                    className="text-warning mt-0.5"
                  />
                  <div>
                    <p className="font-medium text-warning">HR Integration</p>
                    <p className="text-sm text-muted-foreground">
                      This role is synced from {role.hrProvider}. Local changes
                      may be overwritten on the next sync unless you mark them
                      as locally modified.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Check" size={16} />
                  {isEdit ? "Save Changes" : "Create Role"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobRoleModal;
